/**
 * Character Profile section — save, browse, apply, and manage character profiles.
 *
 * Rendered inside the MannequinMorphPanel as an expandable section, following
 * the same glass-card visual conventions as other sidebar content.
 *
 * Row interaction:
 * - Tapping a row opens a confirm-apply popover.
 * - A three-dots menu provides Rename, Overwrite, Duplicate, and Delete.
 */

import {
  Check,
  ChevronDown,
  ChevronRight,
  Plus,
  UserCircle,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLibraryStore } from "../app/libraryStore.ts";
import {
  duplicateLibraryItem,
  loadCharacterFromLibrary,
  overwriteCharacterInLibrary,
  saveCharacterToLibrary,
} from "../app/poseLibrary.ts";
import { Button, IconButton } from "./Button.tsx";
import { showConfirmDialog } from "./ConfirmDialog.tsx";
import { LibraryItemRow } from "./LibraryItemRow.tsx";
import { SearchField } from "./shared.tsx";
import { PANEL_INPUT_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER_STRONG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_SMALL,
  RADIUS_SM,
} from "./tokens.ts";

export function CharacterProfileSection({ objectId }: { objectId: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginTop: 8 }}>
      <Button
        onClick={() => setExpanded((p) => !p)}
        variant="ghost"
        fullWidth
        leftIcon={
          <>
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <UserCircle size={14} />
          </>
        }
        style={{
          gap: 6,
          padding: "4px 0",
          color: COLOR_TEXT_DIM,
          fontSize: FONT_SIZE_SMALL,
          fontWeight: 500,
          justifyContent: "flex-start",
        }}
      >
        Character Profiles
      </Button>
      {expanded && <CharacterProfileContent objectId={objectId} />}
    </div>
  );
}

function CharacterProfileContent({ objectId }: { objectId: string }) {
  const items = useLibraryStore((s) => s.items);
  const fetchAll = useLibraryStore((s) => s.fetchAll);
  const deleteItems = useLibraryStore((s) => s.deleteItems);
  const renameItem = useLibraryStore((s) => s.renameItem);

  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [search, setSearch] = useState("");
  const saveInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const characterItems = useMemo(() => {
    let filtered = items.filter((i) => i.type === "character");
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((i) => i.name.toLowerCase().includes(q));
    }
    return filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [items, search]);

  const handleSave = useCallback(async () => {
    const name =
      newName.trim() || `Character ${new Date().toLocaleDateString()}`;
    setSaving(true);
    await saveCharacterToLibrary(objectId, name);
    setNewName("");
    setShowSaveInput(false);
    setSaving(false);
    fetchAll();
  }, [objectId, newName, fetchAll]);

  const handleApply = useCallback(
    async (fileUrl: string) => {
      await loadCharacterFromLibrary(fileUrl, objectId);
    },
    [objectId],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = await showConfirmDialog(
        "Delete this character? This cannot be undone.",
      );
      if (confirmed) await deleteItems([id]);
    },
    [deleteItems],
  );

  useEffect(() => {
    if (showSaveInput)
      requestAnimationFrame(() => saveInputRef.current?.focus());
  }, [showSaveInput]);

  const inputStyle = PANEL_INPUT_STYLE;

  return (
    <div
      style={{
        marginTop: 6,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {/* Save current character */}
      {!showSaveInput ? (
        <Button
          onClick={() => setShowSaveInput(true)}
          variant="ghost"
          fullWidth
          leftIcon={<Plus size={14} />}
          style={{
            padding: "7px 10px",
            borderRadius: RADIUS_SM,
            border: `1px dashed ${COLOR_BORDER_STRONG}`,
            color: COLOR_TEXT_DIM,
            fontSize: FONT_SIZE_SMALL,
            justifyContent: "flex-start",
          }}
        >
          Save Current
        </Button>
      ) : (
        <div style={{ display: "flex", gap: 4 }}>
          <input
            ref={saveInputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setShowSaveInput(false);
            }}
            placeholder="Character name"
            style={inputStyle}
          />
          <IconButton
            onClick={handleSave}
            disabled={saving}
            icon={<Check size={14} />}
            label="Save character profile"
            style={{ color: COLOR_ACCENT }}
          />
          <IconButton
            onClick={() => setShowSaveInput(false)}
            icon={<X size={14} />}
            label="Cancel"
            style={{ color: COLOR_TEXT_DIM }}
          />
        </div>
      )}

      {/* Search */}
      {characterItems.length > 3 && (
        <SearchField value={search} onChange={setSearch} />
      )}

      {/* Character list */}
      <div
        style={{
          maxHeight: 200,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {characterItems.length === 0 && (
          <div
            style={{
              padding: "10px 4px",
              textAlign: "center",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_SMALL,
            }}
          >
            {search ? "No matches" : "No saved characters"}
          </div>
        )}

        {characterItems.map((item) => (
          <LibraryItemRow
            key={item.id}
            id={item.id}
            name={item.name}
            thumbnailUrl={item.thumbnailUrl}
            thumbnailSize={28}
            thumbnailFallback={
              <UserCircle size={14} style={{ color: COLOR_TEXT_FAINT }} />
            }
            applyLabel="Apply Character"
            onApply={() => item.fileUrl && handleApply(item.fileUrl)}
            onRename={(newName) => renameItem(item.id, newName)}
            onOverwrite={() => overwriteCharacterInLibrary(item.id, objectId)}
            onDuplicate={() => duplicateLibraryItem(item.id)}
            onDelete={() => handleDelete(item.id)}
          />
        ))}
      </div>
    </div>
  );
}
